import { action, observable } from "mobx";
import { FormulaFormVM } from "../template/FormulaFormVM";
import { HydraulicFormulaController } from "cd-eft/services/eftSetApi";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";

interface IBoxInletDropFrom {
  width: string,
  length: string,
  headAboveWeir: string,
  weirElevation: string,
  floorElevation: string,
}
interface IBoxInletDropResults {
  capacityQ?: string;
  errorResponse?: {
    errorCode?: string;
    errorMessage?: string;
    errorType?: string;
  };
  minChannelWidth?: string;
  topZ?: string;
  validationPass?: boolean;
}


export class BoxInletDropVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  constructor(props) {
    super(props);
    // Any Form specific contructor code. Otherwise, remove contructor - use parent.
    this.formLabelsForExport = {
      width: 'Width (ft)',
      length: 'Length (ft)',
      headAboveWeir: 'Head Above Weir (ft)',
      weirElevation: 'Weir Elevation (ft)',
      floorElevation: 'Floor Elevation (ft)',
    }
    this.resultLabelsForExport = {
      capacityQ: 'Capacity Q (cfs)',
      minChannelWidth: 'Minimum Channel Width (ft)',
      topZ: 'Top Elevation (ft)'
    }
  }

  // Expand with onChange handlers, onBlur validation, Submit handler, on submit validation, individual variables, etc.
  @observable boxInletDropFrom: IBoxInletDropFrom = {
    width: this.defaultValues?.width,
    length: this.defaultValues?.length,
    headAboveWeir: this.defaultValues?.headAboveWeir,
    weirElevation: this.defaultValues?.weirElevation,
    floorElevation: this.defaultValues?.floorElevation,
  }
  @observable boxInletDropResults: IBoxInletDropResults = {
    capacityQ: '0',
    minChannelWidth: '0',
    topZ: '0'
  }
  @observable boxInletDropFormInputSnapshot: IBoxInletDropFrom | undefined = undefined
  @observable hasResults: boolean = false;
  @observable formValidation = {
    hasError: true,
    length: false,
    width: false,
    headAboveWeir: false,
    floorElevation: false,
    weirElevation: false,
    formErrors: {
      length: '',
      width: '',
      weirElevation: '',
      headAboveWeir: '',
      floorElevation: ''
    },
  };

  @action.bound handleOnKeyDown(event) {
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
  }

  @action.bound BoxInletDropErrorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage?.length === 0 ? '' : ' usa-input--error';
  }
  @action.bound setFixedValue(name, fixed) {
    const event1 = {
      target: {
        name: name,
        value: this.roundingHelperString(fixed, 2)
      }
    }
    this.handleBoxInletDropForm(event1, true);
  }
  @action.bound async handleBoxInletDropForm(event, within?: boolean) {
    const { name, value } = event.target;
    if (!within && value.length > 7) {
      return
    }
    switch (name) {
      case 'width':
        this.boxInletDropFrom.width = value;
        break;
      case 'length':
        this.boxInletDropFrom.length = value;
        break;
      case 'Weir_Elevation':
        this.boxInletDropFrom.weirElevation = value;
        break;
      case 'Head_Above_Weir':
        this.boxInletDropFrom.headAboveWeir = value;
        break;
      case 'Floor_Elevation':
        this.boxInletDropFrom.floorElevation = value;
        break;
    }
  }
  @action.bound async validateInput(name, value) {
    const val = this.roundingHelperNumber(parseFloat(value), 2);
    if (value !== '' && value !== undefined) this.setFixedValue(name, val);

    switch (name) {
      case 'width':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.width = true;
          this.formValidation.formErrors.width = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.width.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.width = false;
          this.formValidation.formErrors.width = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'length':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.length = true;
          this.formValidation.formErrors.length = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.length.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.length = false;
          this.formValidation.formErrors.length = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'Weir_Elevation':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.weirElevation = true;
          this.formValidation.formErrors.weirElevation = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.weirElevation.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.weirElevation = false;
          this.formValidation.formErrors.weirElevation = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'Head_Above_Weir':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.headAboveWeir = true;
          this.formValidation.formErrors.headAboveWeir = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.headAboveWeir.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.headAboveWeir = false;
          this.formValidation.formErrors.headAboveWeir = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'Floor_Elevation':
        if (val < 0) {
          this.formValidation.floorElevation = true;
          this.formValidation.formErrors.floorElevation = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.floorElevation.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.floorElevation = false;
          this.formValidation.formErrors.floorElevation = '';
          this.formValidation.hasError = false;
        }
        break;
    }
  }
  get validateRequiredInputs() {
    if (this.formValidation.weirElevation || this.formValidation.width ||
      this.formValidation.length || this.formValidation.headAboveWeir) {
      return true
    }
    return false
  }

  @action async validateReqInput() {
    await this.validateInput("Weir_Elevation", this.boxInletDropFrom.weirElevation);
    await this.validateInput("width", this.boxInletDropFrom.width);
    await this.validateInput("length", this.boxInletDropFrom.length);
    await this.validateInput("Head_Above_Weir", this.boxInletDropFrom.headAboveWeir);
  }

  @action  async computeParaCh() {
    this.disableComputeBtn = true;
    this.disablePrintBtn = true;
    this.isLoading = true;
    MessageCenter.blockCalls(['Common_26']);
    await this.validateReqInput();
    if (!this.validateRequiredInputs) {
      try {
        const boxInLetPayload = {
          floorElevation: parseFloat(this.boxInletDropFrom.floorElevation),
          headAboveWeir: parseFloat(this.boxInletDropFrom.headAboveWeir),
          length: parseFloat(this.boxInletDropFrom.length),
          weirElevation: parseFloat(this.boxInletDropFrom.weirElevation),
          width: parseFloat(this.boxInletDropFrom.width),
        }
        const response = await HydraulicFormulaController.calculateBoxDropInletUsingPOST(boxInLetPayload);
        const shortCode = response?.data?.errorResponse?.errorCode?.replace(/[123]_[a-zA-Z]+_[a-zA-Z]+_/, '');
        if (response && response.data) {
          if (response.data.validationPass) {
            this.hasResults = true;
            this.boxInletDropResults = this.removeAndSetPlotData(response.data);
            this.boxInletDropFormInputSnapshot = { ...this.boxInletDropFrom };
          } else if (
            response?.data?.errorResponse?.errorMessage != undefined &&
            response?.data?.errorResponse?.errorType != undefined
          ) {
            if (shortCode === 'Common_10') {
              MessageCenter.resetMessageCenter()
              MessageCenter.addNotification(
                //For common_10 errors for optional fields when negative
                'Highlighted fields ' + response?.data?.errorResponse?.errorMessage,
                response?.data?.errorResponse?.errorType,
                shortCode
              );
            } else {
              MessageCenter.addNotification(
                response?.data?.errorResponse?.errorMessage,
                response?.data?.errorResponse?.errorType,
                shortCode
              );
            }
            this.hasResults = false;
            this.resetPlotData();
          }
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      // If we are missing required inputs or they are invalid
      // If field validation has been added, input specific messages should not be shown at this time.

      MessageCenter.resetMessageCenter();
      await MessageCenter.addNotificationFromCode('Common_26');
      this.hasResults = false;
      this.resetPlotData();
    }
    this.disableComputeBtn = false;
    this.isLoading = false;
    MessageCenter.unblockCalls();
  }

  @action.bound launchAndExport() {
    // disable print button
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];

    for (const input in this.boxInletDropFormInputSnapshot) {
      this.exportedInputs.push({
        label: this.formLabelsForExport[input],
        value: this.boxInletDropFormInputSnapshot[input]
      })
    }
    for (const result in this.boxInletDropResults) {
      if (result !== 'errorResponse' || 'validationPass') {
        const label = this.resultLabelsForExport[result];
        const value = this.boxInletDropResults[result];
        if (label && value !== undefined) {
          this.exportedResults.push({
            label: this.resultLabelsForExport[result],
            value: this.boxInletDropResults[result]
          })
        }
      }
    }
    this.printFormula();
    this.disablePrintBtn = false;
  }

  @action.bound initializeDefaultValues(): void {
    this.boxInletDropFrom = {
      width: this.roundingHelperString(this.defaultValues.width, 2),
      length: this.roundingHelperString(this.defaultValues.length, 2),
      headAboveWeir: this.roundingHelperString(this.defaultValues.headAboveWeir, 2),
      weirElevation: this.roundingHelperString(this.defaultValues.weirElevation, 2),
      floorElevation: this.roundingHelperString(this.defaultValues.floorElevation, 2),
    }
    this.boxInletDropFormInputSnapshot = { ...this.boxInletDropFrom }
  }

}